import Button from '../../../common/Button';
import {
	formFieldHasErrors,
	FormResults,
	genErrorIdFromLabel,
} from '../../../common/Form';
import Typography from '../../../common/Typography';
import { useDescribeAttrActionMutation } from '../../api';
import { AttrActionFormProps } from '../common/commonTypes';
import { renderDerivationSubfields } from '../common/jsxHelpers';
import { StyledFlexContainer, StyledPaper } from '../common/styledComponents';
import {
	describeAttrFormDefaults,
	describeAttrFormToPayload,
} from './describeAttributeHelpers';
import { DescribeAttrFormValues } from './describeAttributeTypes';
import { FunctionComponent, useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

const quantitySubtypeOptions = [
	{ displayValue: 'None', value: '' },
	{ displayValue: 'Distance', value: 'distance' },
	{ displayValue: 'Duration', value: 'duration' },
	{ displayValue: 'Currency', value: 'currency' },
	{ displayValue: 'Ratio', value: 'ratio' },
	{ displayValue: 'Rate', value: 'rate' },
];

const locationSubtypeOptions = [
	{ displayValue: 'Geography', value: 'geography' },
	{ displayValue: 'None', value: '' },
];

const DescribeAttributeForm: FunctionComponent<AttrActionFormProps> = (
	props
) => {
	const formDefaults = describeAttrFormDefaults(props);

	const { handleSubmit, register, formState, watch, resetField } =
		useForm<DescribeAttrFormValues>({
			defaultValues: formDefaults,
		});

	// watch type field to determine what to display in
	// subtype menu
	const watchedAttrType = watch('type', formDefaults.type);

	// when type changes we reset the subtype to make sure we don't get a mismatch between
	// type and subtype values.
	useEffect(() => {
		resetField('otherType');
	}, [watchedAttrType, resetField]);

	const { _id, plural: label } = props;

	const [describeAttr, describeAttrResults] = useDescribeAttrActionMutation();

	const onSubmit: SubmitHandler<DescribeAttrFormValues> = (vals, e) => {
		e?.preventDefault();
		describeAttr({
			sourceAttrId: _id,
			body: describeAttrFormToPayload(vals),
		});
	};

	// only present option to choose a subtype if main type is appropriate
	const renderSubtypeSelect = useCallback(() => {
		const shouldRender =
			watchedAttrType === 'quantity' || watchedAttrType === 'location';

		if (!shouldRender) {
			return null;
		}

		const opts =
			watchedAttrType === 'quantity'
				? quantitySubtypeOptions
				: locationSubtypeOptions;

		return (
			<>
				<label htmlFor="otherType">
					<Typography>
						What is the subtype of this attribute?
					</Typography>
				</label>
				<select {...register('otherType')}>
					{opts.map(({ value, displayValue }) => (
						<option value={value} key={value}>
							{displayValue}
						</option>
					))}
				</select>
			</>
		);
	}, [watchedAttrType, register]);

	return (
		<StyledPaper>
			<form onSubmit={handleSubmit(onSubmit)}>
				<StyledFlexContainer flexDirection="column" alignItems="center">
					{renderDerivationSubfields(true, register, formState)}

					<label htmlFor="type">
						<Typography>
							What type of attribute is {label}?
						</Typography>
					</label>
					<select
						{...register('type', {
							required: 'Type is a required field',
						})}
					>
						<option value="identity">Identity</option>
						<option value="relation">Relation</option>
						<option value="category">Category</option>
						<option value="quantity">Quantity</option>
						<option value="event">Event</option>
						<option value="location">Location</option>
						<option value="media">Media</option>
					</select>

					{renderSubtypeSelect()}

					<label htmlFor="default-value">
						<Typography>
							What is the default value for this attribute?
						</Typography>
					</label>
					<input
						type="text"
						{...register('defaultValue')}
						id="default-value"
					/>

					<Typography id="isRequired">
						Is this attribute required?
					</Typography>
					<div
						role="radiogroup"
						aria-labelledby="isRequired"
						aria-errormessage={genErrorIdFromLabel('isRequired')}
						aria-invalid={formFieldHasErrors(
							'isRequired',
							formState
						)}
					>
						<div>
							<input
								id="isRequired-true"
								type="radio"
								value="true"
								{...register('isRequired', {
									required: {
										value: true,
										message:
											'A value for isRequired is required',
									},
								})}
							/>
							<label htmlFor="isRequired-true">
								<Typography>Yes</Typography>
							</label>
						</div>
						<div>
							<input
								id="isRequired-false"
								type="radio"
								value="false"
								{...register('isRequired', {
									required: {
										value: true,
										message:
											'A value for isRequired is required',
									},
								})}
							/>
							<label htmlFor="isRequired-false">
								<Typography>No</Typography>
							</label>
						</div>
					</div>

					<Button type="submit">Submit</Button>

					<FormResults
						{...describeAttrResults}
						validationErrors={formState.errors}
					/>
				</StyledFlexContainer>
			</form>
		</StyledPaper>
	);
};

export default DescribeAttributeForm;
